// Adapted from OpenCharts (MIT, see LICENSE-OpenCharts).

export interface Pt {
  x: number;
  y: number;
}

export function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Distance from p to the segment a→b. */
export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq === 0) return dist(p, a);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq));
  return dist(p, { x: a.x + t * abx, y: a.y + t * aby });
}

/** Constrain p to the nearest 45° step around anchor (Shift while drawing). */
export function snapAngle(anchor: Pt, p: Pt): Pt {
  const dx = p.x - anchor.x;
  const dy = p.y - anchor.y;
  const r = Math.hypot(dx, dy);
  if (r < 1) return p;
  const step = Math.PI / 4;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  return { x: anchor.x + r * Math.cos(angle), y: anchor.y + r * Math.sin(angle) };
}

/** True if p is inside the box spanned by two corners, grown by tolerance. */
export function pointInBox(p: Pt, x1: number, y1: number, x2: number, y2: number, tolerance: number): boolean {
  return (
    p.x >= Math.min(x1, x2) - tolerance &&
    p.x <= Math.max(x1, x2) + tolerance &&
    p.y >= Math.min(y1, y2) - tolerance &&
    p.y <= Math.max(y1, y2) + tolerance
  );
}

/** [r, g, b] of a hex (#rgb / #rrggbb) or rgb()/rgba() colour; null otherwise. */
function rgbOf(color: string): [number, number, number] | null {
  const hex = /^#([a-f\d]{3}|[a-f\d]{6})$/i.exec(color);
  if (hex) {
    const h = hex[1]!.length === 3 ? [...hex[1]!].map(c => c + c).join("") : hex[1]!;
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i.exec(color);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
}

/** A colour at the given opacity. */
export function withAlpha(color: string, alpha: number): string {
  const c = rgbOf(color);
  return c ? `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})` : color;
}

/** Near-black or white, whichever reads on the given background. */
export function readableInk(background: string): string {
  const c = rgbOf(background);
  if (!c) return "#FFFFFF";
  const lin = c.map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
  // Contrast against white vs against #0B0B0C (luminance ≈ 0.0037).
  return (1.05) / (lum + 0.05) >= (lum + 0.05) / 0.0537 ? "#FFFFFF" : "#0B0B0C";
}
